import { lazy } from 'react';

// After a deploy, Vite emits chunks with new hashed filenames. A browser that
// already holds the old index.html (cached tab) requests the old chunk names,
// which no longer exist on the server, so the route's dynamic import() rejects
// with a ChunkLoadError. The fix is a single hard reload: it pulls the fresh
// index.html that references the new chunk names.
//
// A sessionStorage timestamp guards against a reload loop: if the import still
// fails shortly after a reload, the error is real (not a stale chunk), so we
// rethrow and let ErrorBoundary show the fallback instead of reloading forever.
const RELOAD_KEY = 'chunk-reload-ts';
const RELOAD_WINDOW_MS = 10_000;

// Self-heal reload with a loop guard, shared by the stale-chunk path below and
// ErrorBoundary's render-crash path (one budget for both — a reload is a reload).
// Returns true if a reload was initiated; false if one already happened within
// the window, meaning the error is real and the caller must surface it.
export function hardReloadOnce() {
  let last = 0;
  try { last = Number(sessionStorage.getItem(RELOAD_KEY)) || 0; } catch { /* private mode */ }
  const now = Date.now();
  if (now - last <= RELOAD_WINDOW_MS) return false;
  try { sessionStorage.setItem(RELOAD_KEY, String(now)); } catch { /* ignore */ }
  window.location.reload();
  return true;
}

export function lazyWithReload(factory) {
  return lazy(() =>
    factory().catch((err) => {
      if (hardReloadOnce()) {
        // Keep the route suspended until the reload kicks in, so React never
        // renders the error state for a stale-chunk failure we're recovering from.
        return new Promise(() => {});
      }
      throw err;
    }),
  );
}
